// Single source of truth for the authed-page top header. Used by every
// page mounted behind AuthGate (sources list, source detail, archive,
// onboarding, workspace dashboard, workspaces admin, audit, settings).
//
// Layout (left → right):
//   [DH logo] / [breadcrumb segments] · [optional brand-env]
//                                     · ────────── ←(spacer)
//                                                         WorkspaceSwitcher
//                                                         activity link
//                                                         ProfileMenu (avatar)
//                                                         live indicator
//
// Profile menu surfaces email, super-admin badge, audit log link
// (when applicable), workspaces admin link, and Sign out.
//
// Workspace switcher placement is RIGHT — matches the pattern most SaaS
// dashboards converged on (linear, vercel, gh) where the brand is a
// fixed home anchor and the user's tenant + identity live as a paired
// 'who/where' cluster on the trailing edge.

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import ThemeToggle from './ThemeToggle';
import WhatsNewButton from './WhatsNewButton';
import { getMe, signOut } from '../api';
import type { UserPublic } from '@datahub/shared';

export interface BreadcrumbSegment {
  label: string;
  to?: string;
}

export interface TopHeaderProps {
  /** Breadcrumbs after 'Data Hub.' — e.g. [{label:'Sources', to:'/sources'}, {label:'Acme'}]. */
  breadcrumb?: BreadcrumbSegment[];
  /** Final coloured pill segment (page name). Truncates with ellipsis at 28 chars. */
  env?: string;
  /** Optional right-side extras (e.g. 'live' status pill) inserted before the live indicator. */
  rightExtra?: React.ReactNode;
  /** Hide the live ready indicator (e.g. on partner portal). */
  hideLive?: boolean;
}

export default function TopHeader({ breadcrumb = [], env, rightExtra, hideLive }: TopHeaderProps): React.ReactElement {
  return (
    <div className="topbar">
      <div className="brand">
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 10 }} title="Home">
          <span className="brand-mark">DH</span>
          <span className="brand-name">Data Hub<span className="dot">.</span></span>
        </Link>
        {breadcrumb.map((seg, i) => (
          <React.Fragment key={i}>
            <span className="brand-sep">/</span>
            {seg.to
              ? <Link to={seg.to} style={{ color: 'inherit', textDecoration: 'none' }}>{seg.label}</Link>
              : <span style={{ color: 'var(--rail-ink-2)' }}>{seg.label}</span>}
          </React.Fragment>
        ))}
        {env && (
          <>
            <span className="brand-sep">/</span>
            <span className="brand-env">{env.length > 28 ? `${env.slice(0, 28)}…` : env}</span>
          </>
        )}
      </div>
      <div className="topbar-right" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {rightExtra}
        <WorkspaceSwitcher />
        <Link to="/sources" style={{ color: 'inherit', textDecoration: 'none', fontSize: 12 }} title="All sources in this workspace">sources</Link>
        <Link to="/datasets/new" style={{ color: 'inherit', textDecoration: 'none', fontSize: 12 }} title="Build a cross-source dataset (preview rows, export to BigQuery / manifest)">datasets</Link>
        <Link to="/workspace" style={{ color: 'inherit', textDecoration: 'none', fontSize: 12 }} title="Workspace activity & cost dashboard">activity</Link>
        <WhatsNewButton />
        <ProfileMenu />
        <ThemeToggle />
        {!hideLive && <span className="live"><span className="live-dot" />ready</span>}
      </div>
    </div>
  );
}

/** Avatar + dropdown with email, super-admin badge, audit link
 *  (super-admin only), workspaces link, sign-out. Replaces the
 *  ad-hoc 'sign out' link sprinkled across pages. */
function ProfileMenu(): React.ReactElement | null {
  const [me, setMe] = useState<UserPublic | null | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getMe().then(setMe).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (me === undefined) return null;
  if (me === null) {
    // Unauthenticated path — don't show a profile menu (LandingPage handles login CTA).
    return null;
  }

  // Initials avatar — first letter of name, fallback to first letter of email.
  const initial = (me.name?.trim()?.[0] ?? me.email[0] ?? '?').toUpperCase();

  async function onSignOut(): Promise<void> {
    try {
      await signOut();
    } finally {
      window.location.href = '/';
    }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={`${me.email}${me.is_super_admin ? ' · super-admin' : ''}`}
        style={{
          // Lock width = height + flex-shrink: 0 so the avatar stays
          // a perfect circle even when the topbar is squeezed (long
          // workspace name + email + buttons crowd .topbar-right).
          // Without flexShrink the flex parent could narrow the button
          // below 26px, producing an ellipse (incident 2026-04-30).
          width: 26, minWidth: 26, height: 26, flexShrink: 0,
          borderRadius: '50%',
          background: me.is_super_admin ? 'oklch(0.55 0.18 290)' : 'oklch(0.45 0.05 250)',
          color: 'white',
          border: 'none', cursor: 'pointer',
          fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: 0,
        }}
        aria-label="User menu"
      >
        {me.picture
          // eslint-disable-next-line @next/next/no-img-element
          ? <img src={me.picture} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
          : initial}
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)',
          minWidth: 240, zIndex: 40,
          background: 'var(--bg)', color: 'var(--ink)',
          border: '1px solid var(--line)', borderRadius: 8,
          boxShadow: '0 8px 24px oklch(0.10 0 0 / 0.18)',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{me.name || me.email}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>{me.email}</div>
            {me.is_super_admin && (
              <div style={{ marginTop: 4, fontSize: 10, fontFamily: 'var(--mono)', color: 'oklch(0.55 0.18 290)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>super-admin</div>
            )}
          </div>
          <Link
            to="/workspaces"
            onClick={() => setOpen(false)}
            style={{ display: 'block', padding: '8px 14px', fontSize: 12, color: 'var(--ink)', textDecoration: 'none', borderBottom: '1px solid var(--line)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elev)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >Workspaces &amp; API keys</Link>
          {me.is_super_admin && (
            <Link
              to="/audit"
              onClick={() => setOpen(false)}
              style={{ display: 'block', padding: '8px 14px', fontSize: 12, color: 'var(--ink)', textDecoration: 'none', borderBottom: '1px solid var(--line)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elev)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >Audit log</Link>
          )}
          <button
            onClick={() => { void onSignOut(); }}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '8px 14px', fontSize: 12,
              background: 'transparent', color: 'var(--danger)',
              border: 'none', cursor: 'pointer',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elev)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >Sign out</button>
        </div>
      )}
    </div>
  );
}
